import { useEffect, useState } from "react"
import { Check, Wand2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import {
  applyValueToField,
  isApplySuggestionTargetCurrent,
  isFieldApplyAvailable,
  isFieldEmpty,
  notifySuggestionApplyEvent,
  subscribeFieldApplyEntries,
} from "@/components/Copilot/applySuggestion/applySuggestionHandler"
import ConfirmReplaceDialog from "@/components/Copilot/applySuggestion/ConfirmReplaceDialog"

interface ApplyAllSuggestionsButtonProps {
  applyBlocks: { key: string; value: string }[]
  className?: string
  applyTargetPath?: string
}

export const ApplyAllSuggestionsButton = ({
  applyBlocks,
  className,
  applyTargetPath,
}: ApplyAllSuggestionsButtonProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()

  const [anyAvailable, setAnyAvailable] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isApplied, setIsApplied] = useState(false)

  useEffect(() => {
    const computeAvailability = () => {
      setAnyAvailable(
        isApplySuggestionTargetCurrent(applyTargetPath) &&
          applyBlocks.some((b) => isFieldApplyAvailable(b.key))
      )
    }
    computeAvailability()
    return subscribeFieldApplyEntries(computeAvailability)
  }, [applyTargetPath, applyBlocks])

  const tryApplyAll = async () => {
    if (
      !applyBlocks.length ||
      !isApplySuggestionTargetCurrent(applyTargetPath)
    ) {
      setAnyAvailable(false)
      return
    }
    const requestedKeys = applyBlocks.map((block) => block.key)
    notifySuggestionApplyEvent({
      phase: "start",
      source: "all",
      requestedKeys,
      applyTargetPath,
    })
    let applied = 0
    const appliedKeys: string[] = []
    for (const b of applyBlocks) {
      const ok = await applyValueToField(b.key, b.value)
      if (ok) {
        applied += 1
        appliedKeys.push(b.key)
      }
    }
    notifySuggestionApplyEvent({
      phase: "complete",
      source: "all",
      requestedKeys,
      appliedKeys,
      applyTargetPath,
    })
    if (applied === applyBlocks.length) {
      setIsApplied(true)
      return
    }
    const skipped = applyBlocks.length - applied
    alertToast?.({
      title: t("common.copilot.applyAllFailed"),
      description: t("common.copilot.applyAllPartial", {
        applied,
        total: applyBlocks.length,
        skipped,
      }),
    })
  }

  if (applyBlocks.length === 0) return null

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className={`${className || ""} border-0 bg-zinc-800 text-zinc-100 hover:bg-zinc-700 disabled:opacity-60 disabled:cursor-not-allowed`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (
                !anyAvailable ||
                !isApplySuggestionTargetCurrent(applyTargetPath)
              ) {
                setAnyAvailable(false)
                return
              }
              // Determine if any targeted fields are non-empty
              const targets = applyBlocks.filter((b) =>
                isFieldApplyAvailable(b.key)
              )
              const nonEmpty = targets.filter((b) => !isFieldEmpty(b.key))
              if (nonEmpty.length > 0) {
                setConfirmOpen(true)
                return
              }
              void tryApplyAll()
            }}
            disabled={!anyAvailable || isApplied}
          >
            {isApplied ? (
              <Check className="h-4 w-4 mr-2 text-emerald-400" />
            ) : (
              <Wand2 className="h-4 w-4 mr-2 text-zinc-100" />
            )}
            <span className="text-zinc-100">
              {t(
                isApplied
                  ? "common.copilot.appliedToDraft"
                  : "common.copilot.applyAll"
              )}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          {isApplied
            ? t("common.copilot.appliedToDraftHint")
            : anyAvailable
              ? t("common.copilot.applyAllHint")
              : t("common.copilot.applyAllHintDisabled")}
        </TooltipContent>
      </Tooltip>
      <ConfirmReplaceDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={async () => {
          setConfirmOpen(false)
          await tryApplyAll()
        }}
      />
    </TooltipProvider>
  )
}

export default ApplyAllSuggestionsButton
